import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  KeyRound,
  LogIn,
  RefreshCcw,
  Save,
  Search,
  ShieldOff,
  Trash2,
  UserCog,
} from "lucide-react";
import { useEffect, useState } from "react";

import {
  ImpersonationState,
  OperatorSubject,
  SuperAdministratorSubject,
  api,
  formatDate,
} from "../api";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  MutationMessage,
  PageHeader,
  Section,
  StatusBadge,
  shortId,
} from "../account/components";

function tone(value: string): "neutral" | "good" | "warning" | "danger" {
  if (value === "ACTIVE") return "good";
  if (["LOCKED", "ANONYMIZED"].includes(value)) return "danger";
  if (["SUSPENDED", "DELETION_PENDING", "PENDING_VERIFICATION"].includes(value)) return "warning";
  return "neutral";
}

export function SuperAdminPage({ currentSubjectId }: { currentSubjectId: string }) {
  const client = useQueryClient();
  const [input, setInput] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [identity, setIdentity] = useState({ username: "", displayName: "", email: "", emailVerified: false, locale: "", timeZone: "" });
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [confirmation, setConfirmation] = useState("");

  const subjects = useQuery({
    queryKey: ["super-admin-subjects", query],
    queryFn: () => api<{ subjects: OperatorSubject[] }>(`/api/v1/operator/subjects?query=${encodeURIComponent(query)}&limit=20`),
    enabled: query.length >= 3,
  });
  const results = subjects.data?.subjects ?? [];
  useEffect(() => {
    if (results.length && !results.some((item) => item.id === selected)) setSelected(results[0].id);
  }, [results, selected]);

  const detail = useQuery({
    queryKey: ["super-admin-subject", selected],
    queryFn: () => api<SuperAdministratorSubject>(`/api/v1/operator/super-admin/subjects/${selected}`),
    enabled: Boolean(selected),
  });
  useEffect(() => {
    if (!detail.data) return;
    setIdentity({
      username: detail.data.username ?? "",
      displayName: detail.data.displayName ?? "",
      email: detail.data.email ?? "",
      emailVerified: detail.data.emailVerified,
      locale: detail.data.locale,
      timeZone: detail.data.timeZone,
    });
    setConfirmation("");
  }, [detail.data]);

  function refresh() {
    void client.invalidateQueries({ queryKey: ["super-admin-subject", selected] });
    void client.invalidateQueries({ queryKey: ["super-admin-subjects", query] });
  }

  const update = useMutation({
    mutationFn: () => api<SuperAdministratorSubject>(`/api/v1/operator/super-admin/subjects/${selected}`, {
      method: "PUT",
      body: JSON.stringify({ ...identity, version: detail.data?.version }),
    }),
    onSuccess: refresh,
  });
  const resetPassword = useMutation({
    mutationFn: () => api<void>(`/api/v1/operator/super-admin/subjects/${selected}/password-reset`, {
      method: "POST",
      body: JSON.stringify({ password, reason }),
    }),
    onSuccess: () => { setPassword(""); setReason(""); },
  });
  const resetMfa = useMutation({
    mutationFn: () => api(`/api/v1/operator/super-admin/subjects/${selected}/mfa-reset`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
    onSuccess: () => { setReason(""); refresh(); },
  });
  const revokeAccess = useMutation({
    mutationFn: () => api(`/api/v1/operator/super-admin/subjects/${selected}/revoke-access`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
    onSuccess: () => setReason(""),
  });
  const impersonate = useMutation({
    mutationFn: () => api<ImpersonationState>(`/api/v1/operator/super-admin/subjects/${selected}/impersonation`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
    onSuccess: () => window.location.assign("/account"),
  });
  const anonymize = useMutation({
    mutationFn: () => api<void>(`/api/v1/operator/super-admin/subjects/${selected}/anonymize`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
    onSuccess: () => { setReason(""); setConfirmation(""); refresh(); },
  });

  const target = detail.data;
  const self = target?.id === currentSubjectId;
  const reasonReady = reason.trim().length >= 10;
  const canAuthenticate = target?.status === "ACTIVE" && !self;
  const mutationError = resetPassword.error ?? resetMfa.error ?? revokeAccess.error ?? impersonate.error ?? anonymize.error;

  return <>
    <PageHeader eyebrow="SUPER ADMIN" title="全局账号管理" description="编辑任意账号、重置访问凭据或在受审计的 15 分钟窗口内模拟登录。" />
    <form className="operator-search" onSubmit={(event) => { event.preventDefault(); setQuery(input.trim()); }}>
      <label><span>用户名、邮箱或主体 ID</span><input required minLength={3} maxLength={254} value={input} onChange={(event) => setInput(event.target.value)} /></label>
      <button className="primary-button" type="submit"><Search />查找</button>
    </form>
    {subjects.isPending && query && <LoadingState />}
    {subjects.error && <ErrorState error={subjects.error} retry={() => void subjects.refetch()} />}
    {query && !subjects.isPending && results.length === 0 && <Section title="搜索结果"><EmptyState icon={UserCog} title="没有匹配账号" detail="使用完整邮箱、主体 ID 或至少三个字符的用户名。" /></Section>}
    {results.length > 0 && <div className="operator-workspace super-admin-workspace">
      <div className="operator-record-list">{results.map((item) => <button className={item.id === selected ? "active" : ""} key={item.id} type="button" onClick={() => setSelected(item.id)}><span><strong>{item.displayName ?? item.username ?? "已匿名化账号"}</strong><small>{item.username ? `@${item.username}` : shortId(item.id)}</small></span><StatusBadge tone={tone(item.status)}>{item.status}</StatusBadge></button>)}</div>
      <div>
        {detail.isPending && <LoadingState />}
        {detail.error && <ErrorState error={detail.error} retry={() => void detail.refetch()} />}
        {target && <>
          <Section title={target.displayName ?? target.username ?? "已匿名化账号"} action={<StatusBadge tone={tone(target.status)}>{target.status}</StatusBadge>}>
            <dl className="operator-facts"><div><dt>主体 ID</dt><dd><code>{target.id}</code></dd></div><div><dt>创建时间</dt><dd>{formatDate(target.createdAt)}</dd></div><div><dt>状态原因</dt><dd>{target.statusReason ?? "无"}</dd></div></dl>
            {self && <p className="operator-warning">不能通过最高管理员工作台修改或模拟登录自己的账号。</p>}
            {target.status !== "ANONYMIZED" && <form className="operator-action-form" onSubmit={(event) => { event.preventDefault(); update.mutate(); }}>
              <div className="field-grid"><label><span>用户名</span><input required minLength={3} maxLength={32} disabled={self} value={identity.username} onChange={(event) => setIdentity({ ...identity, username: event.target.value })} /></label><label><span>显示名称</span><input required maxLength={80} disabled={self} value={identity.displayName} onChange={(event) => setIdentity({ ...identity, displayName: event.target.value })} /></label></div>
              <div className="field-grid"><label><span>主邮箱</span><input required type="email" maxLength={254} disabled={self} value={identity.email} onChange={(event) => setIdentity({ ...identity, email: event.target.value })} /></label><label className="operator-check"><input type="checkbox" disabled={self} checked={identity.emailVerified} onChange={(event) => setIdentity({ ...identity, emailVerified: event.target.checked })} /><span>邮箱已验证</span></label></div>
              <div className="field-grid"><label><span>语言</span><input required maxLength={35} disabled={self} value={identity.locale} onChange={(event) => setIdentity({ ...identity, locale: event.target.value })} /></label><label><span>时区</span><input required maxLength={64} disabled={self} value={identity.timeZone} onChange={(event) => setIdentity({ ...identity, timeZone: event.target.value })} /></label></div>
              <button className="primary-button" disabled={self || update.isPending} type="submit"><Save />保存账号</button>
              <MutationMessage error={update.error} success={update.isSuccess && "账号资料已更新"} />
            </form>}
          </Section>

          {!self && target.status !== "ANONYMIZED" && <Section title="安全与访问" description={`TOTP ${target.totpEnabled ? "已启用" : "未启用"} / 通行密钥 ${target.passkeyCount} / 恢复码 ${target.recoveryCodeCount}`}>
            <label><span>操作理由</span><textarea required minLength={10} maxLength={500} rows={2} value={reason} onChange={(event) => setReason(event.target.value)} /></label>
            <div className="super-admin-actions">
              <label><span>设置新密码</span><input type="password" autoComplete="new-password" maxLength={1024} disabled={!canAuthenticate} value={password} onChange={(event) => setPassword(event.target.value)} /></label>
              <button className="secondary-button" disabled={!canAuthenticate || !reasonReady || !password || resetPassword.isPending} type="button" onClick={() => resetPassword.mutate()}><KeyRound />重置密码</button>
              <button className="secondary-button" disabled={!canAuthenticate || !reasonReady || resetMfa.isPending} type="button" onClick={() => resetMfa.mutate()}><RefreshCcw />重置全部 MFA</button>
              <button className="secondary-button" disabled={!reasonReady || revokeAccess.isPending} type="button" onClick={() => revokeAccess.mutate()}><ShieldOff />撤销会话与令牌</button>
              <button className="primary-button" disabled={!canAuthenticate || !reasonReady || impersonate.isPending} type="button" onClick={() => impersonate.mutate()}><LogIn />模拟登录 15 分钟</button>
            </div>
            <MutationMessage error={mutationError} success={(resetPassword.isSuccess && "密码已重置，现有访问已撤销") || (resetMfa.isSuccess && "MFA 已重置") || (revokeAccess.isSuccess && "访问已撤销")} />
          </Section>}

          {!self && target.status !== "ANONYMIZED" && <Section title="立即匿名化" description="不可恢复。账号标识、邮箱和认证凭据将立即移除，用户名进入保留期。">
            <div className="operator-danger-zone"><label><span>输入用户名确认</span><input value={confirmation} onChange={(event) => setConfirmation(event.target.value)} placeholder={target.username ?? ""} /></label><button className="danger-button" disabled={!reasonReady || confirmation !== target.username || anonymize.isPending} type="button" onClick={() => anonymize.mutate()}><Trash2 />立即匿名化</button></div>
            <MutationMessage error={anonymize.error} success={anonymize.isSuccess && "账号已匿名化"} />
          </Section>}
        </>}
      </div>
    </div>}
  </>;
}
